<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import AuditService from '@/services/quality/audit.service'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

definePage({
  meta: {
    name: 'incident-master-audit',
    subject: 'quality.menu',
    action: 'master_audit',
  },
})

const router = useRouter()

const typeOptions = await getEnumValue('AuditType', true)

const resolveTypeLabel = key => {
  if (!key) return 'N/A'

  return typeOptions.value.find(option => option.value === key)?.title ?? key
}

const headers = ref([
  {
    title: 'Title',
    key: 'title',
  },
  {
    title: 'Type',
    key: 'type',
  },
  {
    title: 'Frequency',
    key: 'frequency',
  },
  {
    title: 'Users',
    key: 'users',
    sortable: false,
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
])

const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 500)
const itemsPerPage = ref(10)
const pageNumber = ref(1)
const sortBy = ref()
const orderBy = ref()

const {
  isFetching,
  data: auditsData,
  execute: fetchAudits,
} = useApi(createUrl('/incident/audits', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page: pageNumber,
    sort: sortBy,
    order_by: orderBy,
    with: ['users'],
  },
}))

const audits = computed(() => auditsData.value?.data ?? auditsData.value ?? [])
const totalAudits = computed(() => auditsData.value?.meta?.total ?? auditsData.value?.length ?? 0)

const navigateToSetup = (id = null) => {
  if (id) {
    router.push({ name: 'app-quality-master-audit-setup', params: { id } })
  } else {
    router.push({ name: 'app-quality-master-audit-setup' })
  }
}

// Delete
const isConfirmDialogVisible = ref(false)
const deletingAudit = ref(null)
const refConfirmDialog = ref()

const showConfirmDialog = item => {
  deletingAudit.value = item
  isConfirmDialogVisible.value = true
}

const deleteAudit = async (confirm, data) => {
  if (data?.id && confirm) {
    await AuditService.destroy(
      data.id,
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchAudits()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, value => {
  if (!value) deletingAudit.value = null
})
</script>

<template>
  <VCard
    variant="flat"
    class="border"
  >
    <VCardText class="d-flex flex-wrap py-4 gap-4">
      <div style="inline-size: 12rem;">
        <AppTextField
          v-model="searchQuery"
          placeholder="Search"
          density="compact"
        />
      </div>

      <VSpacer />

      <div class="d-flex align-center gap-4">
        <VBtn
          variant="flat"
          prepend-icon="tabler-plus"
          @click="navigateToSetup(null)"
        >
          Add Audit
        </VBtn>

        <IconBtn
          variant="tonal"
          class="rounded"
          :disabled="isFetching"
          @click="fetchAudits"
        >
          <VIcon icon="tabler-refresh" />
        </IconBtn>
      </div>
    </VCardText>

    <VDivider />

    <VDataTableServer
      v-model:items-per-page="itemsPerPage"
      v-model:page="pageNumber"
      :items-length="totalAudits"
      :items="audits"
      :headers="headers"
      fixed-header
      class="text-no-wrap"
      :loading="isFetching"
    >
      <template #item.title="{ item }">
        {{ item.title }}
      </template>
      <template #item.type="{ item }">
        <span>{{ resolveTypeLabel(item.type) }}</span>
      </template>
      <template #item.frequency="{ item }">
        <span class="text-capitalize">{{ item.frequency ?? 'N/A' }}</span>
      </template>
      <template #item.users="{ item }">
        <div
          v-if="item.users?.length > 0"
          class="v-avatar-group"
        >
          <VAvatar
            v-for="user in item.users"
            :key="user.id"
            size="32"
            :color="$vuetify.theme.current.dark ? '#4A5072' : '#f6f6f7'"
          >
            <VTooltip
              activator="parent"
              location="top"
            >
              {{ user.name }}
            </VTooltip>
            <VImg
              v-if="user.avatar"
              :src="user.avatar"
            />
            <span v-else>
              {{ avatarText(user.name) }}
            </span>
          </VAvatar>
        </div>
        <span
          v-else
          class="text-medium-emphasis"
        >N/A</span>
      </template>
      <template #item.status="{ item }">
        <VChip
          :color="item.status ? 'success' : 'secondary'"
          size="small"
        >
          {{ item.status ? 'Active' : 'Inactive' }}
        </VChip>
      </template>
      <template #item.actions="{ item }">
        <IconBtn @click="navigateToSetup(item.id)">
          <VIcon icon="tabler-edit" />
        </IconBtn>
        <IconBtn @click="showConfirmDialog(item)">
          <VIcon icon="tabler-trash" />
        </IconBtn>
      </template>
    </VDataTableServer>
  </VCard>

  <ConfirmDialog
    ref="refConfirmDialog"
    v-model:isDialogVisible="isConfirmDialogVisible"
    confirmation-question="Are you sure you want to delete this audit?"
    confirm-msg="Audit Deleted."
    confirm-title="Deleted!"
    cancel-title="Error!"
    cancel-msg="Something Went Wrong."
    :is-confirm-visible="false"
    :is-error-visible="false"
    :data="deletingAudit"
    @confirm="deleteAudit"
  />
</template>

<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
